import React, { createContext, useContext, useState } from "react";
import authService from "../services/AuthService";
import { ILoginData, IUserInfo } from "../types/authTypes";

export interface AuthContextState {
  isAuthenticated: boolean;
  userInfo?: IUserInfo;
}

interface AuthContextType {
  state: AuthContextState;
  login?: (data: ILoginData, cb: (response: string) => void) => Promise<void>;
  logout?: () => void;
}

export const initialAuthState: AuthContextState = {
  isAuthenticated: false,
  userInfo: undefined,
};

const AuthContext = createContext<AuthContextType>({
  state: initialAuthState,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    authService.isLoggedIn(),
  );
  const [userInfo, setuserInfo] = useState(authService.getUserInfo());

  const login = async (data: ILoginData, cb: (res: string) => void) => {
    try {
      const authResponse = await authService.login({
        clinic_id: data.clinicId,
        password: data.password,
        username: data.username,
        role: data.role,
        clinicContact: data.clinicContact,
        userType: data.userType,
      });
      if (authResponse.result === "success") {
        const userInfo: IUserInfo = {
          clinicId: data.clinicId,
          clinicName: data.clinicName,
          userName: data.username,
          userType: data.userType,
          clinicContact: data.clinicContact,
        };
        authService.setUserInfo(userInfo);
        setIsAuthenticated(true);
        setuserInfo(userInfo);
        cb(authResponse.result);
      } else {
        cb(authResponse.result);
      }
    } catch (e) {
      console.log("Catch", e);
      return;
    }
  };

  const logout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        state: { isAuthenticated, userInfo },
        login: login,
        logout: logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

//design hook for this context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
